// DocumentSearchResult — one `role="option"` row (CTSK-493 §5.3;
// design doc §3.3). Pure render: typed result in, markup out. No state,
// no fetching — the dropdown owns focus, the container owns navigation.
//
// CTSK-494 Phase 1c: the row BODY (badge / title / snippet / tags+date)
// moved to DocumentHighlightRow so the Documents page's full-page search
// mode renders the same thing without inheriting combobox semantics.
// This file keeps the option shell exactly as Phase 1b shipped it:
// id for aria-activedescendant, aria-selected focus ring, the
// mousedown-eat so the input never blurs before the click lands.

import type { DocumentSearchResult as SearchResult } from '@captain-tasko/shared';
import { optionIdFor } from './highlight.js';
import { DocumentHighlightRow } from './DocumentHighlightRow.js';
import type { LabelKind } from './labelChips.js';

export { MAX_INLINE_TAGS, formatUpdated } from './DocumentHighlightRow.js';

export interface DocumentSearchResultProps {
  result: SearchResult;
  /** Keyboard focus ring (aria-selected). The input keeps DOM focus. */
  focused: boolean;
  onSelect: (result: SearchResult) => void;
  /** Mouse hover moves the keyboard focus ring (standard combobox UX). */
  onFocusRequest?: (result: SearchResult) => void;
  /** Hide the nest badge (the row sits under a labelled group header). */
  hideNestBadge?: boolean;
  /** CTSK-507 — a label chip was clicked. */
  onLabelFilter?: (kind: LabelKind, value: string) => void;
}

export function DocumentSearchResult({
  result,
  focused,
  onSelect,
  onFocusRequest,
  hideNestBadge = false,
  onLabelFilter,
}: DocumentSearchResultProps) {
  return (
    <div
      id={optionIdFor(result)}
      role="option"
      aria-selected={focused}
      className={`doc-search__option${focused ? ' doc-search__option--focused' : ''}`}
      data-testid="document-search-result"
      // mousedown would blur the input (closing the dropdown before the
      // click lands) — standard combobox trick: eat it, select on click.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onSelect(result)}
      onMouseEnter={() => onFocusRequest?.(result)}
    >
      <DocumentHighlightRow result={result} hideNestBadge={hideNestBadge} onLabelFilter={onLabelFilter} />
    </div>
  );
}
